import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers";
import axios, { AxiosError } from "axios";
import { useEffect } from "react";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import { Clubs, Risorse, RisorseTipologie } from "../prismaTypes";
import { SettimanaRisorsa } from "./SettimanaRisorsa";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import { uiStore } from "../uiStore";
import memoize from "lodash-es/memoize";
import { useParams } from "react-router-dom";
import { oraPrenotaStore } from "../oraPrenotaStore";
import { Alert, Button, ButtonGroup, Link, Typography, useMediaQuery } from "@mui/material";
import { Link as Link2 } from "react-router-dom";
import format from "date-fns/format";
import { it } from "date-fns/locale";
import { useTheme } from "@mui/material/styles";
import { prenotaStore } from "../prenotaStore";
import { Legenda } from "../prenotazione/Legenda";
import LoopIcon from "@mui/icons-material/Loop";

const getGiorni = memoize((value2: number | undefined) => {
  if (value2 === undefined) return [];

  const value = new Date(value2);
  const giorni: Date[] = [];

  if (value && value !== null) {
    let day = value.getDay();
    let lunedi = new Date(value);

    while (day !== 1) {
      lunedi.setDate(lunedi.getDate() - 1);
      day = lunedi.getDay();
    }
    giorni.push(new Date(lunedi));

    let domenica = new Date(lunedi);
    domenica.setDate(domenica.getDate() + 1);

    day = domenica.getDay();
    while (day !== 1) {
      giorni.push(new Date(domenica));
      domenica.setDate(domenica.getDate() + 1);
      day = domenica.getDay();
    }
    giorni.forEach((g) => {
      g.setHours(0);
      g.setUTCMinutes(0);
      g.setUTCSeconds(0);
      g.setUTCMilliseconds(0);
    });
  }

  return giorni;
});

export function Prenotazioni() {
  const { prenota } = useParams();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const modPrenota = window.location.href.includes("/prenotazione") || prenota === "prenota";

  const viewSet = prenotaStore((state) => state.viewSet);
  const setViewSet = prenotaStore((state) => state.setViewSet);
  const settimana = prenotaStore((state) => state.settimana);
  const setSettimana = prenotaStore((state) => state.setSettimana);

  const { isLoading: loadingClub } = useQuery<{ club: Clubs }, AxiosError>(
    ["club/getByDomain"],
    () => axios.get(API_URL + "club/getByDomain").then((res) => res.data),
    {
      enabled: !viewSet,
      onSuccess(data) {
        setViewSet(true);
        setSettimana(!data.club.visGiornaliera);
      },
    }
  );

  const isSettimana = settimana;

  const isAuthenticated =
    oraPrenotaStore((state) => state.isAuthenticated()) &&
    !window.location.href.includes("/prenotazione/");

  const value = prenotaStore((s) => s.giornoPrenotazione);
  const setValue = prenotaStore((s) => s.setGiornoPrenotazione);

  const { isLoading, data } = useQuery<
    unknown,
    AxiosError,
    { risorse: (Risorse & { tipologia: RisorseTipologie })[] }
  >(["risorse/findAll"], () => axios.get(API_URL + "risorse/findAll").then((res) => res.data), {
    refetchOnWindowFocus: false,
  });

  const setTitle = uiStore((state) => state.setTitle);
  useEffect(() => {
    setTitle(modPrenota ? "Prenotazioni" : "Tariffe");
  }, [setTitle, modPrenota]);

  const dateStr = value?.getTime();
  let giorni: Date[] = [];
  if (isSettimana) giorni = value !== null && value.getFullYear() > 1990 ? getGiorni(dateStr) : [];
  else if (value) {
    const val = value;
    val.setHours(0, 0, 0, 0);
    giorni = [val];
  }

  const setGiorno = (day: number) => {
    const days = value !== null && value.getFullYear() > 1990 ? getGiorni(dateStr) : [];
    if (days.length > 5) setValue(days[day]);
  };

  if (isLoading || loadingClub) return <>Loading...</>;
  if (!data) return <></>;

  const prev = () => {
    if (value !== null) setValue(new Date(value.getTime() - 7 * 24 * 3600 * 1e3));
  };
  const next = () => {
    if (value !== null) setValue(new Date(value.getTime() + 7 * 24 * 3600 * 1e3));
  };

  const oggiDay = giorni.length > 0 ? giorni[0]?.getDay() ?? -1 : -1;

  return (
    <>
      {!isAuthenticated && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: "700px" }}>
          Per prenotare è necessario autenticarsi &nbsp;
          <Link component={Link2} to={"/login"}>
            [clicca qui]
          </Link>
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={3}
        justifyContent={["center", "center", "flex-start"]}
        alignItems="center"
        sx={{ flexWrap: "wrap" }}
      >
        <Button color="secondary" startIcon={<LoopIcon />} onClick={() => setSettimana(!settimana)}>
          Cambia Vista
        </Button>

        <Legenda />

        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton aria-label="settimana precedente" onClick={() => prev()}>
            <ArrowBackIcon />
          </IconButton>

          <DatePicker
            value={value}
            onChange={(newValue) => setValue(newValue)}
            renderInput={(params) => (
              <TextField {...params} variant="standard" sx={{ width: 150 }} type="date" />
            )}
          />

          <IconButton aria-label="settimana successiva" onClick={() => next()}>
            <ArrowForwardIcon />
          </IconButton>
        </Stack>
      </Stack>

      {value && value !== null && value.getFullYear() > 1980 && (
        <>
          {!isSettimana && (
            <Stack
              direction="column"
              justifyContent="center"
              alignItems={["center", "center", "flex-start"]}
            >
              <Typography sx={{ mt: 1 }}>
                {format(value, "cccc dd, MMMM yyyy", { locale: it })}
              </Typography>
              <ButtonGroup
                variant="outlined"
                sx={{ mt: 1 }}
                orientation={isSmall ? "vertical" : "horizontal"}
              >
                <Button
                  variant={oggiDay === 1 ? "contained" : "outlined"}
                  onClick={() => setGiorno(0)}
                >
                  Lunedì
                </Button>
                <Button
                  variant={oggiDay === 2 ? "contained" : "outlined"}
                  onClick={() => setGiorno(1)}
                >
                  Martedì
                </Button>
                <Button
                  variant={oggiDay === 3 ? "contained" : "outlined"}
                  onClick={() => setGiorno(2)}
                >
                  Mercoledì
                </Button>
                <Button
                  variant={oggiDay === 4 ? "contained" : "outlined"}
                  onClick={() => setGiorno(3)}
                >
                  Giovedì
                </Button>
                <Button
                  variant={oggiDay === 5 ? "contained" : "outlined"}
                  onClick={() => setGiorno(4)}
                >
                  Venerdì
                </Button>
                <Button
                  variant={oggiDay === 6 ? "contained" : "outlined"}
                  onClick={() => setGiorno(5)}
                >
                  Sabato
                </Button>
                <Button
                  variant={oggiDay === 0 ? "contained" : "outlined"}
                  onClick={() => setGiorno(6)}
                >
                  Domenica
                </Button>
              </ButtonGroup>
            </Stack>
          )}
          <Stack
            direction="row"
            gap={2}
            sx={{ mt: 2, flexWrap: "wrap" }}
            justifyContent={["center", "center", "flex-start"]}
          >
            {data &&
              data.risorse.map((r) => (
                <Box key={r.id + (modPrenota ? "P" : "T")} sx={{ maxWidth: "100vw" }}>
                  <SettimanaRisorsa
                    settimana={isSettimana}
                    risorsa={r}
                    giorni={giorni}
                    modPrenota={modPrenota}
                    isAuthenticated={isAuthenticated}
                  />
                </Box>
              ))}
          </Stack>
        </>
      )}
    </>
  );
}
